// Draws the "red flag" share card entirely in the browser. Nothing is uploaded or stored.
const W = 1080, H = 1350;

const band = (s) => s >= 75 ? '#f87171' : s >= 50 ? '#fbbf24' : s >= 25 ? '#facc15' : '#34d399';

export const shareUrl = () => `${window.location.origin}/?ref=share`;

export const cardFacts = (d) => {
  const score = Math.round(d.risk_score ?? d.overall_risk ?? 0);
  const label = d.risk_label || (score >= 75 ? 'Severe' : score >= 50 ? 'Elevated' : score >= 25 ? 'Moderate' : 'Safe');
  const summary = d.one_line_summary || d.one_liner || d.reason || '';
  const tactics = [
    ...(d.detected_tactics || []).map(t => t.tactic),
    ...(d.red_flags || []).map(f => f.flag),
    ...(d.behavioral_risks || []).map(b => b.risk),
    ...(d.tactics_glossary || []).map(t => t.tactic),
    ...(d.dark_patterns || []).map(p => p.pattern),
    ...(d.patterns || []).map(p => p.pattern),
  ].filter(Boolean).slice(0, 3);
  return { score, label, summary, tactics };
};

export const shareText = (d) => {
  const { score, label, summary, tactics } = cardFacts(d);
  const t = tactics.length ? `\nTactics: ${tactics.join(', ')}` : '';
  return `RED FLAG READ · ${score}/100 · ${label}\n${summary}${t}\n\nCheck your own chats free at unmanipulated — ${shareUrl()}`;
};

function wrap(ctx, text, maxW, maxLines) {
  const words = String(text).split(/\s+/);
  const lines = [];
  let cur = '';
  for (const w of words) {
    const test = cur ? `${cur} ${w}` : w;
    if (ctx.measureText(test).width > maxW && cur) { lines.push(cur); cur = w; } else cur = test;
    if (lines.length === maxLines) break;
  }
  if (lines.length < maxLines && cur) lines.push(cur);
  if (lines.length === maxLines && words.join(' ') !== lines.join(' ')) lines[maxLines - 1] = lines[maxLines - 1].replace(/\s*\S*$/, '…');
  return lines;
}

export async function renderShareCard(d) {
  try { await document.fonts.load('900 120px Chivo'); await document.fonts.load('700 30px "IBM Plex Mono"'); } catch {}
  const { score, label, summary, tactics } = cardFacts(d);
  const c = document.createElement('canvas');
  c.width = W; c.height = H;
  const ctx = c.getContext('2d');
  const color = band(score);

  ctx.fillStyle = '#09090b'; ctx.fillRect(0, 0, W, H);
  ctx.strokeStyle = '#27272a'; ctx.lineWidth = 2;
  for (let x = 0; x < W; x += 90) { ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, H); ctx.globalAlpha = 0.25; ctx.stroke(); }
  ctx.globalAlpha = 1;
  ctx.fillStyle = color; ctx.fillRect(0, 0, W, 14);

  ctx.fillStyle = '#a1a1aa'; ctx.font = '700 26px "IBM Plex Mono"'; ctx.textBaseline = 'top';
  ctx.fillText('R E D   F L A G   R E A D', 80, 80);
  ctx.fillStyle = '#34d399'; ctx.fillText('● unmanipulated', W - 80 - ctx.measureText('● unmanipulated').width, 80);

  ctx.fillStyle = color; ctx.font = '900 300px Chivo';
  ctx.fillText(String(score), 70, 150);
  const scoreW = ctx.measureText(String(score)).width;
  ctx.fillStyle = '#52525b'; ctx.font = '900 70px Chivo'; ctx.fillText('/100', 80 + scoreW, 360);

  ctx.fillStyle = color; ctx.fillRect(80, 480, 18, 18);
  ctx.fillStyle = '#fafafa'; ctx.font = '700 34px "IBM Plex Mono"';
  ctx.fillText(label.toUpperCase().split('').join(' '), 118, 470);

  ctx.fillStyle = '#fafafa'; ctx.font = '900 64px Chivo';
  let y = 560;
  for (const line of wrap(ctx, summary || 'Read it before you reply.', W - 160, 4)) { ctx.fillText(line, 80, y); y += 76; }

  if (tactics.length) {
    y += 30;
    ctx.fillStyle = '#71717a'; ctx.font = '700 24px "IBM Plex Mono"'; ctx.fillText('T A C T I C S   S P O T T E D', 80, y); y += 50;
    ctx.font = '700 30px "IBM Plex Mono"';
    let x = 80;
    for (const t of tactics) {
      const txt = t.toUpperCase();
      const w = ctx.measureText(txt).width + 48;
      if (x + w > W - 80) { x = 80; y += 74; }
      ctx.strokeStyle = color; ctx.lineWidth = 3; ctx.strokeRect(x, y, w, 58);
      ctx.fillStyle = color; ctx.fillText(txt, x + 24, y + 13);
      x += w + 16;
    }
  }

  ctx.fillStyle = '#18181b'; ctx.fillRect(0, H - 190, W, 190);
  ctx.fillStyle = color; ctx.fillRect(0, H - 190, W, 3);
  ctx.fillStyle = '#fafafa'; ctx.font = '900 44px Chivo'; ctx.fillText('Check your own chats. Free.', 80, H - 150);
  ctx.fillStyle = '#a1a1aa'; ctx.font = '700 26px "IBM Plex Mono"';
  ctx.fillText(shareUrl().replace(/^https?:\/\//, '').replace('/?ref=share', ''), 80, H - 90);
  ctx.fillStyle = '#fbbf24'; ctx.font = '900 24px Chivo';
  const credit = "A Lyin' Eyes Production";
  ctx.fillText(credit, W - 80 - ctx.measureText(credit).width, H - 140);

  const blob = await new Promise(res => c.toBlob(res, 'image/png'));
  return { blob, dataUrl: c.toDataURL('image/png') };
}
